import React from 'react';
import { Clock } from 'lucide-react';
import { DriverFreezeTypeBadge, DriverMultiloadBadge, driverHasMultiload } from './DriverFreezeTypeBadge';
import { formatFleetClock } from '../utils/fleetWindows';

const FLEET_BOX_LINES = 5;
const FLEET_LINE_PX = 13;

function clean(value) {
  return String(value ?? '').trim();
}

function isBlankId(value) {
  const text = clean(value).toLowerCase();
  return !text || text === '—' || text === '-' || text === 'n/a' || text === 'na';
}

export function simpleVehicleParts(driver) {
  const los = clean(driver?.fleet_los_title);
  const space = clean(driver?.fleet_space_type_title);
  const left = [los, space ? `(${space})` : ''].filter(Boolean).join(' ');
  const rawSeats = driver?.fleet_capacity;
  const seats = rawSeats != null && clean(rawSeats) !== ''
    ? (/s$/i.test(clean(rawSeats)) ? clean(rawSeats).toUpperCase() : `${clean(rawSeats)}S`)
    : '';
  const title = [left, seats].filter(Boolean).join(' · ');
  return { left, seats, title };
}

function fleetVin(window, index) {
  const rig = clean(window?.rig_no || window?.fleet_reg);
  const los = clean(window?.los_title);
  if (isBlankId(rig) || (los && rig.toLowerCase() === los.toLowerCase())) {
    return `V${index + 1}`;
  }
  return rig;
}

export function fleetScheduleLines(driver, timezone = 'America/Los_Angeles') {
  const windows = [...(driver?.fleet_windows || [])].sort(
    (a, b) => (a.window_index ?? 0) - (b.window_index ?? 0)
  );
  return windows.map((window, index) => {
    const vin = fleetVin(window, index);
    const start = formatFleetClock(window.start, timezone) || '—';
    const end = formatFleetClock(window.end, timezone) || '—';
    return { vin, start, end, label: `${vin} ( ${start} - ${end} )` };
  });
}

/** Five lines at the normal size. Past that, the same box gets a smaller font. */
export function fleetScheduleStyle(count) {
  const n = Math.max(Number(count) || 0, 1);
  if (n <= FLEET_BOX_LINES) {
    return { fontSize: '10px', lineHeight: `${FLEET_LINE_PX}px` };
  }
  const lineHeight = (FLEET_BOX_LINES * FLEET_LINE_PX) / n;
  return {
    fontSize: `${Math.max(6, lineHeight * 0.82)}px`,
    lineHeight: `${lineHeight}px`,
    maxHeight: `${FLEET_BOX_LINES * FLEET_LINE_PX}px`,
    overflow: 'hidden',
  };
}

export default function DriverCardIdentity({
  driver,
  fleetMode,
  shiftLabel = '',
  timezone = 'America/Los_Angeles',
  trailing = null,
}) {
  const name = driver?.driver_name || 'Driver';
  const vehicle = simpleVehicleParts(driver);
  const photo = driver?.profileImageURL || (!fleetMode ? driver?.fleet_los_image_url : '') || '';
  const freezeType = driver?.freeze_type;
  const showBadges = freezeType === 'freeze' || freezeType === 'freeze_pre_assign' || freezeType === 'preserved' || driverHasMultiload(driver);
  const showShift = !fleetMode && shiftLabel && shiftLabel !== '—';
  const fleetLines = fleetMode ? fleetScheduleLines(driver, timezone) : [];

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <div className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-white/20 bg-white/5 ring-1 ring-white/10">
          {photo ? (
            <img src={photo} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs font-black uppercase">
              {name.charAt(0)}
            </div>
          )}
        </div>
        <h3 className="min-w-0 flex-1 truncate text-xs font-semibold uppercase leading-none tracking-tight" title={name}>
          {name}
        </h3>
        {showBadges ? (
          <div className="flex shrink-0 items-center gap-1">
            <DriverFreezeTypeBadge freezeType={freezeType} />
            <DriverMultiloadBadge driver={driver} />
          </div>
        ) : null}
        {showShift ? (
          <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-normal tabular-nums leading-none tracking-tight text-white/55" title={`Shift: ${shiftLabel}`}>
            <Clock className="h-3 w-3 shrink-0" strokeWidth={2} aria-hidden />
            {shiftLabel}
          </span>
        ) : null}
        {fleetLines.length ? (
          <div
            className="w-[11rem] shrink-0 text-right tabular-nums tracking-tight"
            style={fleetScheduleStyle(fleetLines.length)}
            title={fleetLines.map((line) => line.label).join('\n')}
          >
            {fleetLines.map((line, index) => (
              <div key={`${index}-${line.label}`} className="flex items-center justify-end gap-1 truncate">
                <span className="font-bold">{line.vin}</span>
                <span className="inline-flex min-w-0 items-center gap-0.5 font-normal text-white/55">
                  <span>(</span>
                  <Clock className="h-[1em] w-[1em] shrink-0" strokeWidth={2} aria-hidden />
                  <span className="truncate">{line.start} - {line.end}</span>
                  <span>)</span>
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {trailing}
      </div>
      {!fleetMode && (vehicle.left || vehicle.seats) ? (
        <div
          className="flex items-center justify-between gap-2 rounded border border-white/10 bg-white/15 px-2 py-0.5"
          title={vehicle.title}
        >
          <span className="min-w-0 flex-1 truncate text-left text-[8.5px] font-black uppercase tracking-tight">
            {vehicle.left}
          </span>
          {vehicle.seats ? (
            <span className="shrink-0 text-[8.5px] font-black tabular-nums tracking-tight">
              {vehicle.seats}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
